"use client";

import { useState, useRef } from "react";
import Link from "next/link";
import { Menu, X, ChevronDown, Phone, ShieldCheck } from "lucide-react";

// Renk opaklığı için yardımcı fonksiyon
const hexToRgba = (hex: string, alpha = 1) => {
    let r = 0, g = 0, b = 0;
    if (hex && (hex.length === 4 || hex.length === 7)) {
        if (hex.length === 4) {
            r = parseInt("0x" + hex[1] + hex[1]);
            g = parseInt("0x" + hex[2] + hex[2]);
            b = parseInt("0x" + hex[3] + hex[3]);
        } else if (hex.length === 7) {
            r = parseInt("0x" + hex[1] + hex[2]);
            g = parseInt("0x" + hex[3] + hex[4]);
            b = parseInt("0x" + hex[5] + hex[6]);
        }
    }
    return `rgba(${r},${g},${b},${alpha})`;
};

// Yeni mimariye göre Props tanımları
interface HeaderProps {
    settings: any;
    products: any[];
}

export default function Header({ settings, products = [] }: HeaderProps) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [productsOpen, setProductsOpen] = useState(false);
    const [productsMobileOpen, setProductsMobileOpen] = useState(false);
    const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Ayarlardan gelen renkler (yoksa varsayılanlar)
    const primaryColor = settings?.themeConfig?.primaryColor || settings?.primaryColor || "#2563eb";
    const secondaryColor = settings?.themeConfig?.secondaryColor || settings?.secondaryColor || "#1e40af";

    // --- YENİ EKLENEN: LOGO YÖNETİMİ ---
    const logos = settings?.logos || {};

    // Masaüstü için öncelik: headerLogo -> logo
    const desktopLogo = logos.headerLogo || logos.logo;

    // Mobil için öncelik: mobileLogo -> logo
    const mobileLogo = logos.mobileLogo || logos.logo;


    const openProducts = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        setProductsOpen(true);
    };

    const closeProducts = () => {
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        closeTimerRef.current = setTimeout(() => {
            setProductsOpen(false);
            closeTimerRef.current = null;
        }, 150);
    };

    const cancelClose = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    const onProductsKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setProductsOpen((v) => !v);
        } else if (e.key === "Escape") {
            setProductsOpen(false);
        }
    };

    // Ürünleri kategorilere ayır (2 sütun için)
    const midIndex = Math.ceil(products.length / 2);
    const leftColumnProducts = products.slice(0, midIndex);
    const rightColumnProducts = products.slice(midIndex);

    return (
        <header className="fixed top-4 left-1/2 z-50 w-[95%] max-w-7xl -translate-x-1/2 rounded-2xl bg-white/90 backdrop-blur-xl border border-white/40 shadow-xl px-4 sm:px-6 py-3 flex items-center justify-between transition-all duration-300 hover:bg-white/95">

            {/* Logo */}
            <div className="font-bold text-xl sm:text-2xl text-transparent bg-clip-text"
                style={{ backgroundImage: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` }}>
                <Link href="/">
                    {/* Eğer logo varsa Masaüstü ve Mobil için ayrı resimleri göster, yoksa site adını yaz */}
                    {desktopLogo && mobileLogo ? (
                        <>
                            {/* Masaüstünde Görünecek Logo */}
                            {desktopLogo && (
                                <img
                                    src={desktopLogo}
                                    alt={settings?.siteName || "Logo"}
                                    className="hidden md:block h-10 w-auto object-contain"
                                />
                            )}
                            {/* Mobilde Görünecek Logo */}
                            {mobileLogo && (
                                <img
                                    src={mobileLogo}
                                    alt={settings?.siteName || "Logo"}
                                    className="block md:hidden h-8 w-auto object-contain"
                                />
                            )}
                        </>
                    ) : (
                        // Hiç logo yoksa sadece yazıyla site adını göster
                        <span>{settings?.siteName || "XYZ Sigorta"}</span>
                    )}
                </Link>
            </div>

            {/* Desktop Menü */}
            <nav className="hidden md:flex items-center space-x-8 font-medium">
                <Link href="/" className="text-gray-700 hover:opacity-80 transition-opacity"
                    onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                    onMouseLeave={(e) => e.currentTarget.style.color = ''}>
                    Anasayfa
                </Link>
                <Link href="/hakkimizda" className="text-gray-700 hover:opacity-80 transition-opacity"
                    onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                    onMouseLeave={(e) => e.currentTarget.style.color = ''}>
                    Hakkımızda
                </Link>

                {/* Ürünler dropdown */}
                <div
                    className="relative"
                    onMouseEnter={openProducts}
                    onMouseLeave={closeProducts}
                >
                    <button
                        aria-haspopup="true"
                        aria-expanded={productsOpen}
                        onKeyDown={onProductsKeyDown}
                        className="text-gray-700 flex items-center transition-colors"
                        onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                        onMouseLeave={(e) => e.currentTarget.style.color = ''}
                    >
                        Ürünler <ChevronDown size={18} className={`ml-1 transition-transform ${productsOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown İçeriği */}
                    <div
                        onMouseEnter={cancelClose}
                        onMouseLeave={closeProducts}
                        className={`absolute top-full left-1/2 mt-4 w-125 bg-white/95 backdrop-blur-md text-gray-800 rounded-xl shadow-xl p-6 grid grid-cols-2 gap-4 z-50 transition-all duration-200 origin-top ${productsOpen
                            ? "opacity-100 scale-100 pointer-events-auto -translate-x-1/2"
                            : "opacity-0 scale-95 pointer-events-none -translate-x-1/2"
                            }`}
                    >
                        {products.length === 0 ? (
                            <div className="col-span-2 py-8 text-center">
                                <p className="text-sm text-gray-500">Henüz ürün bulunmuyor.</p>
                            </div>
                        ) : (
                            <>
                                {/* Sol Kolon */}
                                <ul className="space-y-2">
                                    <li>
                                        <Link
                                            href="/urunler"
                                            className="block py-2 px-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                                            style={{ '--hover-color': hexToRgba(primaryColor, 0.1) } as React.CSSProperties}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hexToRgba(primaryColor, 0.05)}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                                        >
                                            Tüm Sigortalar
                                        </Link>
                                    </li>
                                    {leftColumnProducts.map((product) => (
                                        <li key={product.id}>
                                            <Link
                                                href={`/urunler/${product.slug}`}
                                                className="block py-2 px-3 rounded-lg transition-colors"
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hexToRgba(primaryColor, 0.05)}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                                            >
                                                {product.name || product.title}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>

                                {/* Sağ Kolon */}
                                <ul className="space-y-2">
                                    {rightColumnProducts.map((product) => (
                                        <li key={product.id}>
                                            <Link
                                                href={`/urunler/${product.slug}`}
                                                className="block py-2 px-3 rounded-lg transition-colors"
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hexToRgba(primaryColor, 0.05)}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                                            >
                                                {product.name || product.title}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}

                        {/* Ters ok */}
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-5 h-5 bg-white/95 rotate-45"></div>
                    </div>
                </div>

                {/* İletişim vs Blog */}
                <Link href="/blog" className="text-gray-700 transition-opacity"
                    onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                    onMouseLeave={(e) => e.currentTarget.style.color = ''}>
                    Blog
                </Link>
            </nav>

            {/* Sağ Taraf Butonlar (Desktop) */}
            <div className="hidden md:flex items-center space-x-3">
                <Link href="/iletisim" className="px-4 py-2.5 text-gray-700 transition-colors duration-200 flex items-center rounded-lg hover:bg-gray-100"
                    onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                    onMouseLeave={(e) => e.currentTarget.style.color = ''}>
                    <Phone size={18} className="mr-2" /> İletişim
                </Link>

                <Link href="/teklif-al">
                    <button className="px-5 py-2.5 rounded-xl text-white font-semibold shadow-md flex items-center transition-transform hover:scale-105"
                        style={{ backgroundImage: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` }}>
                        <ShieldCheck size={18} className="mr-2" /> Teklif Al
                    </button>
                </Link>
            </div>

            {/* Mobil Menü Butonu */}
            <button
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setMobileOpen((v) => !v)}
                aria-label="open mobile menu"
            >
                {mobileOpen ? <X size={24} color="#374151" /> : <Menu size={24} color="#374151" />}
            </button>

            {/* Mobil Menü Açılır */}
            {mobileOpen && (
                <div className="absolute top-full left-0 mt-3 w-full bg-white/95 backdrop-blur-md rounded-2xl shadow-xl flex flex-col items-center space-y-0 py-4 md:hidden z-50 max-h-[80vh] overflow-y-auto">
                    <Link
                        href="/"
                        onClick={() => setMobileOpen(false)}
                        className="text-gray-800 transition-colors py-3 px-6 w-full text-center border-b border-gray-100"
                        onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                        onMouseLeave={(e) => e.currentTarget.style.color = ''}
                    >
                        Anasayfa
                    </Link>

                    <Link
                        href="/hakkimizda"
                        onClick={() => setMobileOpen(false)}
                        className="text-gray-800 transition-colors py-3 px-6 w-full text-center border-b border-gray-100"
                        onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                        onMouseLeave={(e) => e.currentTarget.style.color = ''}
                    >
                        Hakkımızda
                    </Link>

                    {/* Mobil Ürünler Dropdown */}
                    <div className="w-full border-b border-gray-100">
                        <button
                            onClick={() => setProductsMobileOpen(!productsMobileOpen)}
                            className="text-gray-800 transition-colors py-3 px-6 w-full text-center flex items-center justify-center"
                            onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                            onMouseLeave={(e) => e.currentTarget.style.color = ''}
                        >
                            Ürünler <ChevronDown size={18} className={`ml-2 transition-transform ${productsMobileOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {productsMobileOpen && (
                            <div className="bg-gray-50/80 py-3 text-gray-800 px-4 grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                                {products.length === 0 ? (
                                    <p className="py-4 text-center text-sm text-gray-500">Henüz ürün bulunmuyor.</p>
                                ) : (
                                    <>
                                        <Link
                                            href="/urunler"
                                            className="block py-2 px-3 rounded-lg text-sm text-center font-semibold hover:bg-gray-100"
                                            onClick={() => setMobileOpen(false)}
                                        >
                                            Tüm Sigortalar
                                        </Link>
                                        {products.map((product) => (
                                            <Link
                                                key={product.id}
                                                href={`/urunler/${product.slug}`}
                                                className="block py-2 px-3 rounded-lg text-sm text-center hover:bg-gray-100"
                                                onClick={() => setMobileOpen(false)}
                                            >
                                                {product.name || product.title}
                                            </Link>
                                        ))}
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <Link
                        href="/blog"
                        onClick={() => setMobileOpen(false)}
                        className="text-gray-800 transition-colors py-3 px-6 w-full text-center border-b border-gray-100"
                        onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                        onMouseLeave={(e) => e.currentTarget.style.color = ''}
                    >
                        Blog
                    </Link>

                    <Link
                        href="/police-iptal"
                        onClick={() => setMobileOpen(false)}
                        className="text-gray-800 transition-colors py-3 px-6 w-full text-center border-b border-gray-100"
                        onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                        onMouseLeave={(e) => e.currentTarget.style.color = ''}
                    >
                        Poliçe İptal
                    </Link>

                    <div className="flex flex-col space-y-3 mt-4 px-6 w-full">
                        <Link
                            href="/iletisim"
                            onClick={() => setMobileOpen(false)}
                            className="px-4 py-3 text-gray-700 flex items-center justify-center text-sm rounded-lg bg-gray-100 border border-gray-200 transition-colors"
                            onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                            onMouseLeave={(e) => e.currentTarget.style.color = ''}
                        >
                            <Phone size={16} className="mr-2" /> İletişim
                        </Link>

                        <Link
                            href="/teklif-al"
                            onClick={() => setMobileOpen(false)}
                            className="px-4 py-3 rounded-xl text-white font-semibold text-sm flex items-center justify-center shadow-md"
                            style={{ backgroundImage: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` }}
                        >
                            <ShieldCheck size={16} className="mr-2" /> Teklif Al
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}